import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from "react";
import { Form, message } from "antd";
import BaseModal from "components/BaseModal";
import { FieldTypes, generateFormFields, nameRegexValidator, urlRegexValidator } from "utils/formUtils";
import VersionOneMappingForm from "./VersionOneMappingForm";
import _ from "lodash";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import ModalUtil from "utils/modalUtil";
import { MODAL_SIZE_MEDIUM } from "constants/Layout";
import { duplicateNameValidator } from "utils/validatorUtil";
import { hasPermission } from "utils/authService";
import { PermissionSetMap } from "constants/RBACConstants";

function VersionOneConfigForm({ name, displayName, loginConfigList, ...props }, ref) {
  const isEdit = !_.isUndefined(props.config);
  const [form] = Form.useForm();
  const thisRef = useRef();

  useImperativeHandle(ref, () => ({
    ...thisRef,
  }));

  const onOk = useCallback(async () => {
    await form.validateFields();
    try {
      const toolName = name.toUpperCase().replace(" ", "_");
      let payload = form.getFieldsValue();
      payload = {
        ...payload,
        ticketSystemType: "VERSION_ONE",
        tokenType: "PERSONAL_ACCESS_TOKEN",
      };

      const configUrl = !isEdit ? "postTicketSystemToolLoginDetails" : "updateTicketSystemToolLoginDetails";

      const response = await ToolsIntegrationsAPI[configUrl](toolName, payload, props.config?.id).promise;

      message.success(`${displayName} Login Configuration created`);
      if (!isEdit) {
        ModalUtil.show({
          content: (
            <VersionOneMappingForm
              onUpdateConfigComponent={props.onUpdateConfigComponent}
              name={name}
              loginConfigId={response.id}
              displayName={displayName}
            />
          ),
        });
      }
      props.onUpdateConfigComponent && props.onUpdateConfigComponent(true);
    } catch (err) {
      console.log(err);
      if (!_.isEmpty(err.response.data.errors)) {
        const { field, defaultMessage } = err.response?.data?.errors[0];
        form.setFields([
          {
            name: field,
            errors: [defaultMessage],
          },
        ]);
      }
      return Promise.reject(err.response?.data?.message);
    }
  },[form, isEdit, name, props, displayName])

  const modalProps = {
    title: (
      <>
        {isEdit ? "Edit" : "Add"} {displayName} Login Credentials{" "}
      </>
    ),
    width: MODAL_SIZE_MEDIUM,
    disabled: !hasPermission(isEdit ? PermissionSetMap.EDIT_TOOL_CONFIG : PermissionSetMap.WRITE_TOOLS),
    onOk: onOk,
  };

  const fields = useMemo(
    () => [
      {
        name: "name",
        label: "Name",
        colSpan: 12,
        required: true,
        props: {
          autoFocus: true,
        },
        rules: [
          duplicateNameValidator({ list: loginConfigList }),
          nameRegexValidator,
        ],
      },
      {
        name: "url",
        label: "URL",
        colSpan: 12,
        required: true,
        rules: [urlRegexValidator()],
      },
      {
        name: "apiToken",
        label: "API Token",
        type: FieldTypes.PASSWORD,
        colSpan: 12,
        required: true,
      },
    ],
    [loginConfigList]
  );

  return (
    <BaseModal
      modalProps={modalProps}
      ref={thisRef}
      afterClose={props.afterClose}
    >
      <Form initialValues={props.config} form={form} layout="vertical">
        {generateFormFields(fields)}
      </Form>
    </BaseModal>
  );
}

export default forwardRef(VersionOneConfigForm);
